import gql from 'graphql-tag';

/**
* GraphQL query for validating reCAPTCHA token
*/
const VALIDATE_RECAPTCHA = gql`
    query ValidateRecaptcha($RecaptchaResponse: String!) {
        validateRecaptcha(RecaptchaResponse: $RecaptchaResponse)
    }`;

/**
* Service class to handle reCAPTCHA validation
* @class RecaptchaService
*/
export class RecaptchaService {
    /**
     * Creates an instance of RecaptchaService
     * @param {Object} appStore - The MobX app store instance
     */
    constructor(appStore) {
        this.appStore = appStore;
    }

    /**
     * Validates reCAPTCHA token with server
     * @async
     * @returns {Promise<boolean>} Returns true if validation successful, false otherwise
     * @throws {Error} Throws error if token generation or validation fails
     * @description
     * This method handles the complete reCAPTCHA validation flow:
     * 1. Checks if reCAPTCHA is enabled
     * 2. Generates a token using reCAPTCHA v3
     * 3. Verifies the token with server via GraphQL
     * 4. Updates app store with validation status and any errors
     */
    async validateRecaptcha() {
        const { appStore } = this;

        // Early return if reCAPTCHA not enabled
        if (!appStore.captchaSiteKey || !appStore.enableRecaptcha) {
            return true;
        }

        try {
            // Generate token
            let token;
            try {
                token = await window.grecaptcha.execute(appStore.captchaSiteKey, { action: 'submit' });
                if (!token) {
                    throw new Error('Failed to generate reCAPTCHA token');
                }
            } catch (error) {
                appStore.captchaVerificationError = 'Failed to initialize reCAPTCHA. Please refresh and try again.';
                appStore.isCaptchaVerified = false;
                return false;
            }

            appStore.setCaptchaToken(token);

            // Verify token
            try {
                const result = await appStore.apolloClient.query({
                    query: VALIDATE_RECAPTCHA,
                    variables: { RecaptchaResponse: token },
                    fetchPolicy: 'no-cache',
                    errorPolicy: 'all'
                });

                if (result.errors) {
                    throw new Error('ReCAPTCHA verification service error: ' + result.errors[0].message);
                }

                if (result.data.validateRecaptcha) {
                    appStore.isCaptchaVerified = true;
                    appStore.captchaVerificationError = null;
                    return true;
                } else {
                    appStore.isCaptchaVerified = false;
                    appStore.captchaVerificationError = 'Failed to verify reCAPTCHA. Please try again.';
                    return false;
                }
            } catch (error) {
                if (error.networkError) {
                    appStore.captchaVerificationError = 'Network error during verification. Please check your connection.';
                } else if (error.graphQLErrors?.length > 0) {
                    appStore.captchaVerificationError = error.graphQLErrors[0].message;
                } else {
                    appStore.captchaVerificationError = 'An unexpected error occurred during verification.';
                }
                appStore.isCaptchaVerified = false;
                return false;
            }
        } catch (error) {
            appStore.captchaVerificationError = 'An unexpected error occurred. Please try again.';
            appStore.isCaptchaVerified = false;
            return false;
        }
    }
}
